/*
 * AnationZ search: turns what a shopper types into a precise Shopify search.
 *
 * Shopify's own search also returns "related" products, so "black shirt" matched almost the whole catalogue (pants,
 * blue and pink shirts). This file rewrites the query before it is sent:
 *   - product words  -> title terms   ("shirt" -> title:shirt, "pants" -> title:pant OR title:chino, "shorts" -> type)
 *   - colour words   -> the store's colour filter on the results page ("black" -> Colour: Black)
 *                       and the brand's colour names in the drop-down suggestions (black -> Midnight Onyx)
 *   - sizes          -> the size filter ("xl", "32")
 *   - other words    -> title terms   ("essential chinos" -> title:chino AND title:essential)
 * If the precise search finds nothing (a typo), the page falls back to the normal search. Style codes and SKUs
 * (4+ digit numbers) are left to Shopify. Kill switch: add ?anz_search=off to any URL (?anz_search=on to re-enable).
 */
(() => {
  const DEFAULT_ON = true;
  const FLAG_KEY = 'anz-search';
  const MEMORY_KEY = 'anz-search:';
  const FRESH_KEY = 'anz-search-fresh';
  const SEARCH_PATH = `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}search`;
  const COLOUR_FILTER = 'filter.v.t.shopify.color-pattern';
  const SIZE_FILTER = 'filter.v.option.size';

  // Plain colour words -> the store's standard colours (Black 1, Blue 2, White 3, Gold 4, Silver 5, Beige 6, Brown 7,
  // Gray 8, Green 9, Orange 10, Pink 11, Purple 12, Red 13, Yellow 14, Navy 15).
  const COLOUR_WORDS = {
    black: [1], white: [3], offwhite: [3], cream: [3, 6], blue: [2, 15], navy: [15], grey: [8, 5], gray: [8, 5],
    charcoal: [8], silver: [5], green: [9], khaki: [6], beige: [6], brown: [7], red: [13], maroon: [13], pink: [11],
    purple: [12], violet: [12], orange: [10], yellow: [14], gold: [4, 14],
  };

  // Standard colour -> the colour names used in AnationZ product titles (from the store's product data, 2026-09-22).
  // Only the drop-down suggestions use these; the results page uses the colour filter itself.
  const COLOUR_NAMES = {
    1: ['midnight onyx', 'black'],
    2: ['atlantic blue', 'chalk mist', 'cobalt shore', 'glacial blue', 'midnight ink', 'open sky', 'pale horizon', 'pearl blue', 'teal current', 'blue'],
    3: ['aged ivory', 'alpine white', 'chalk mist', 'clean white', 'pure canvas', 'raw canvas', 'white'],
    4: ['burnished amber', 'gold'],
    5: ['silver'],
    6: ['aged ivory', 'desert khaki', 'driftwood', 'latte', 'natural tan', 'warm sand', 'weathered linen', 'beige'],
    7: ['chocolate', 'cocoa earth', 'dark caramel', 'driftwood', 'espresso', 'light brown', 'warm tobacco', 'brown'],
    8: ['graphite', 'iron mist', 'light grey', 'slate fog', 'stone fog', 'storm dust', 'grey'],
    9: ['deep canopy', 'forest pine', 'matcha', 'moss reserve', 'olive ground', 'pistachio', 'sage breeze', 'olive', 'green'],
    10: ['burnt sienna', 'terracotta flame', 'orange'],
    11: ['coral', 'peach', 'rose dusk', 'pink'],
    12: ['crushed violet', 'ember clay', 'purple'],
    13: ['burgundy reserve', 'royal crimson', 'red'],
    14: ['cream', 'mustard', 'yellow'],
    15: ['atlantic blue', 'deep ocean navy', 'midnight ink', 'navy'],
  };

  const CATEGORY_WORDS = {
    shirt: 'title:shirt', shirts: 'title:shirt',
    tee: 'title:tee', tees: 'title:tee', tshirt: 'title:tee', tshirts: 'title:tee',
    pant: '(title:pant OR title:chino)', pants: '(title:pant OR title:chino)',
    trouser: '(title:pant OR title:chino)', trousers: '(title:pant OR title:chino)',
    chino: 'title:chino', chinos: 'title:chino',
    short: 'product_type:Short', shorts: 'product_type:Short',
    kurta: 'title:kurta', kurtas: 'title:kurta',
    shortsleeve: '(title:short AND title:sleeve)', longsleeve: '(title:long AND title:sleeve)',
  };

  const STOP_WORDS = new Set(['for', 'men', 'mens', 'man', 'the', 'a', 'an', 'and', 'with', 'in', 'of', 'to', 'on', 'my',
    'anationz', 'size', 'colour', 'color', 'light', 'dark']);
  const SIZE_WORDS = { xs: 'XS', s: 'S', m: 'M', l: 'L', xl: 'XL', xxl: '2XL', '2xl': '2XL', xxxl: '3XL', '3xl': '3XL',
    '4xl': '4XL', '5xl': '5XL' };

  const flagIsOn = () => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('anz_search');
      if (fromUrl === 'on' || fromUrl === 'off') window.localStorage.setItem(FLAG_KEY, fromUrl);
      const stored = window.localStorage.getItem(FLAG_KEY);
      return stored ? stored === 'on' : DEFAULT_ON;
    } catch (error) {
      return DEFAULT_ON;
    }
  };

  const remember = (query, original) => {
    try {
      window.sessionStorage.setItem(MEMORY_KEY + query, original);
      window.sessionStorage.setItem(FRESH_KEY, query);
    } catch (error) { /* private mode */ }
  };

  // True only on the first search page load after a translated search (not after the shopper changes filters)
  const takeFresh = (query) => {
    try {
      const fresh = window.sessionStorage.getItem(FRESH_KEY) === query;
      window.sessionStorage.removeItem(FRESH_KEY);
      return fresh;
    } catch (error) { return false; }
  };

  const recall = (query) => {
    try { return window.sessionStorage.getItem(MEMORY_KEY + query); } catch (error) { return null; }
  };

  const nameClause = (name) => {
    const words = name.split(' ').map((word) => `title:${word}`);
    return words.length > 1 ? `(${words.join(' AND ')})` : words[0];
  };

  /**
   * Translate free text into { changed, q, filters, suggestQ }. q + filters drive the results page; suggestQ (no
   * filters) drives the drop-down suggestions.
   */
  const translate = (text) => {
    const original = (text || '').trim();
    const unchanged = { changed: false, q: original, filters: [], suggestQ: original };
    if (!original || original.includes(':') || /\d{4,}/.test(original)) return unchanged;

    const normalised = original.toLowerCase()
      .replace(/['’]/g, '')
      .replace(/\bt[\s-]?shirts?\b/g, ' tshirt ')
      .replace(/\boff[\s-]?white\b/g, ' offwhite ')
      .replace(/\b(short|half)[\s-]?sleeves?\b/g, ' shortsleeve ')
      .replace(/\b(long|full)[\s-]?sleeves?\b/g, ' longsleeve ');
    const tokens = normalised.split(/[^a-z0-9]+/).filter(Boolean);

    const clauses = [];
    const suggestClauses = [];
    const colours = [];
    const sizes = [];

    tokens.forEach((token) => {
      if (STOP_WORDS.has(token)) return;
      if (CATEGORY_WORDS[token]) {
        if (!clauses.includes(CATEGORY_WORDS[token])) clauses.push(CATEGORY_WORDS[token]);
      } else if (COLOUR_WORDS[token]) {
        colours.push(...COLOUR_WORDS[token]);
      } else if (SIZE_WORDS[token]) {
        sizes.push(SIZE_WORDS[token]);
      } else if (/^\d{2}$/.test(token) && Number(token) >= 26 && Number(token) <= 44) {
        sizes.push(token);
      } else if (/^[a-z0-9]+$/.test(token)) {
        clauses.push(`title:${token}`);
      }
    });

    if (!clauses.length && !colours.length && !sizes.length) return unchanged;

    const uniqueColours = [...new Set(colours)];
    const filters = uniqueColours.map((id) => [COLOUR_FILTER, `gid://shopify/TaxonomyValue/${id}`])
      .concat([...new Set(sizes)].map((size) => [SIZE_FILTER, size]));

    suggestClauses.push(...clauses);
    if (uniqueColours.length) {
      const names = [...new Set(uniqueColours.flatMap((id) => COLOUR_NAMES[id] || []))];
      if (names.length) suggestClauses.push(`(${names.map(nameClause).join(' OR ')})`);
    }

    return {
      changed: true,
      q: clauses.length ? clauses.join(' AND ') : '*',
      filters,
      suggestQ: suggestClauses.length ? suggestClauses.join(' AND ') : original,
    };
  };

  const searchUrl = (translation, extraParams) => {
    const params = new URLSearchParams(extraParams || '');
    params.set('q', translation.q);
    params.delete(COLOUR_FILTER);
    params.delete(SIZE_FILTER);
    translation.filters.forEach(([name, value]) => params.append(name, value));
    return `${SEARCH_PATH}?${params.toString()}`;
  };

  const go = (original, extraParams) => {
    const translation = translate(original);
    if (!translation.changed) return false;
    remember(translation.q, original.trim());
    window.location.assign(searchUrl(translation, extraParams));
    return true;
  };

  // Every search form on the site (header search, search page, 404 page)
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!flagIsOn() || !(form instanceof HTMLFormElement) || !form.elements.q) return;
    if (new URL(form.action, window.location.href).pathname !== SEARCH_PATH) return;
    const params = new URLSearchParams(new FormData(form));
    params.delete('q');
    if (go(form.elements.q.value, params)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  // Plain links to a search (e.g. the query suggestions in the drop-down)
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('a[href]');
    if (!flagIsOn() || !link || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== SEARCH_PATH || url.searchParams.has('anz')) return;
    const query = url.searchParams.get('q');
    url.searchParams.delete('q');
    if (query && go(query, url.searchParams)) event.preventDefault();
  }, true);

  // Search page: show the shopper's own words instead of the rewritten query, and fall back to the normal search when
  // the precise one finds nothing.
  const onSearchPage = () => {
    if (window.location.pathname !== SEARCH_PATH) return;
    const params = new URLSearchParams(window.location.search);
    const query = params.get('q');
    const original = query && recall(query);
    if (!original) return;

    if (takeFresh(query) && document.querySelector('[data-anz-no-results]')) {
      window.location.replace(`${SEARCH_PATH}?q=${encodeURIComponent(original)}&anz=raw`);
      return;
    }

    document.querySelectorAll('[data-anz-search-terms]').forEach((element) => {
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        walker.currentNode.nodeValue = walker.currentNode.nodeValue.split(query).join(original);
      }
    });
    document.querySelectorAll('input[name="q"]').forEach((input) => {
      if (input.value === query) input.value = original;
    });
    document.title = document.title.split(query).join(original);
  };

  if (flagIsOn()) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', onSearchPage);
    } else {
      onSearchPage();
    }
  }

  window.anzSearch = {
    translate,
    isOn: flagIsOn,
    /**
     * Used by the theme's predictive search: fetch suggestions for the precise query, and for the shopper's own words
     * when the precise one finds nothing. fetchFor(query) must resolve to the section HTML.
     */
    async suggest(value, fetchFor) {
      const translation = translate(value);
      if (!flagIsOn() || !translation.changed || translation.suggestQ === value.trim()) return fetchFor(value);
      const html = await fetchFor(translation.suggestQ);
      return html.includes('predictive-search__no-results') ? fetchFor(value) : html;
    },
  };
})();
